import ProductHeading from '@components/Product/Details/_ProductHeading';
import Breadcrumb from '@repo/ui/src/components/Breadcrumb';
import Markdown from '@repo/ui/src/components/Markdown';
import { MediaType } from '@repo/ui/src/types/image';
import { LinkProps } from '@repo/ui/src/types/link';
import { PDPTemplate } from '@views/ProductDetailPage/_enums';
import Product from '@models/product';
import dynamic from 'next/dynamic';
import React, { useCallback, useContext, useState } from 'react';
import { AlternateColorProps } from '@components/Product/Details/_ProductSelection';
import { ProductContext } from '@context/ProductProvider';
import GalleryDialog from '@views/ProductDetailPage/_GalleryDialog';
import { BreadcrumbParentProps } from '@repo/ui/src/types/breadcrumb';
import { classNames } from '@repo/ui/src/utils/classNames';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';

const PDPHeroCarousel = dynamic(() => import('@components/Product/Details/_PDPHeroCarousel'));
const SpinSetWrapper = dynamic(() => import('./_SpinSetWrapper'));
const PromotionsAndPricing = dynamic(
  () => import('@components/Product/Details/_PromotionsAndPricing'),
);
const ProductSelection = dynamic(() => import('@components/Product/Details/_ProductSelection'));
const TierOneProductSelection = dynamic(
  () => import('@components/Product/Details/_TierOneProductSelection'),
);
const TierTwoProductSelection = dynamic(
  () => import('@components/Product/Details/_TierTwoProductSelection'),
);
const TierThreeProductSelection = dynamic(
  () => import('@components/Product/Details/_TierThreeProductSelection'),
);

export interface ProductPageProps {
  template: PDPTemplate;
  breadcrumb: BreadcrumbParentProps;
  spinSetUrl?: string;
  product: Product;
  dark?: boolean;
  lifestyleImages?: Array<MediaType>;
  lifestyleVideos?: Array<MediaType>;
  alternateSizes?: {
    sizes: Array<LinkProps>;
  };
  alternateColorsV2?: AlternateColorProps;
  promoMarkdown?: string;
  prop65?: {
    title: string;
    warningBody: string;
  };
  ownerManual?: {
    ownerManualLink: Array<LinkProps>;
  };
  essentials?: Array<{
    id: string;
  }>;
  sizes: Array<Product>;
  customizable?: boolean;
  compatibleWith?: {
    image: MediaType;
    link: LinkProps;
  };
}

const BACKGROUND_IMAGE = {
  [PDPTemplate.NonGrillLight]: 'tw-bg-speckle',
};

const DARK_BACKGROUND_IMAGE = {
  [PDPTemplate.PDPV2]: 'tw-bg-corrugatedMetal',
  [PDPTemplate.TierOne]: 'tw-bg-corrugatedMetal',
  [PDPTemplate.NonGrillDark]: 'tw-bg-woodGrain',
  [PDPTemplate.NonGrillLight]: 'tw-bg-speckle',
  [PDPTemplate.Grill]: 'tw-bg-hardwoodPanel',
};

const ProductPage = ({
  breadcrumb,
  spinSetUrl,
  product,
  template,
  lifestyleImages = [],
  essentials,
  prop65,
  ownerManual,
  promoMarkdown,
  sizes,
  alternateColorsV2,
  customizable,
  dark,
  compatibleWith,
}: ProductPageProps) => {
  const { productList } = useContext(ProductContext);
  const PDPV2 = template === PDPTemplate.PDPV2;
  const NonGrillDark = template === PDPTemplate.NonGrillDark;
  const ZRGrill = template === PDPTemplate.Grill;
  const PDPV2Light = template === PDPTemplate.PDPV2Light;
  const whiteImageBg =
    (template === PDPTemplate.TierOne || template === PDPTemplate.TierTwo) && !dark;
  const darkMode = PDPV2 || NonGrillDark || ZRGrill || dark;
  const display =
    productList.filter((product) => product.checked && product.overlayImage).length > 0;
  const encodedSpinSetUrl = spinSetUrl ? encodeURI(spinSetUrl) : null;
  const [showDialog, setShowDialog] = useState(false);
  const [dialogImages, setDialogImages] = useState([]);
  const [useImagePadding, setUseImagePadding] = useState(false);
  const openGalleryDialog = useCallback((images: any, padding: any) => {
    setDialogImages(images);
    setUseImagePadding(padding);
    setShowDialog(true);
  }, []);

  const hasSpinSet = encodedSpinSetUrl && product.transparentImages;
  const isNewTemplate = [PDPTemplate.TierOne, PDPTemplate.TierTwo, PDPTemplate.TierThree].includes(
    template,
  );

  const showPrice =
    ![PDPTemplate.PDPV2, PDPTemplate.PDPV2Light, PDPTemplate.TierOne].includes(template) &&
    product?.ctaButtonType !== 'noCTA';

  return (
    <div
      className={classNames(
        'sm:tw-relative tw-items-start',
        darkMode ? 'tw-bg-neutral-800' : 'tw-bg-white',
      )}
    >
      {breadcrumb && (
        <Breadcrumb {...breadcrumb} dark={darkMode} title={product.title} className="tw-z-50" />
      )}
      <div className="tw-grid tw-gap-y-6 md:tw-grid-cols-[1fr_calc(554px+8vw)] tw-items-start tw-auto-cols-fr">
        <div
          className={classNames(
            'md:tw-sticky tw-top-[45px] tw-grid tw-gap-y-6 md:tw-grid-flow-col',
            !hasSpinSet && 'md:tw-pt-[120px] md:tw-pb-16',
            whiteImageBg && 'tw-bg-white',
            PDPV2Light && 'tw-bg-warm-100',
          )}
        >
          <div
            className={classNames(
              'tw-pb-8 md:tw-pb-0 md:tw-pt-8 md:tw-hidden tw-grid tw-gap-y-2 tw-pt-5',
              isNewTemplate && !dark && 'tw-bg-warm-100',
            )}
          >
            <ProductHeading
              product={product}
              className={classNames('md:tw-relative', PDPV2Light && 'tw-bg-warm-100')}
              dark={darkMode}
              hideDescription
              hideSKU={template === PDPTemplate.TierThree}
            />
            {showPrice && (
              <PromotionsAndPricing
                promotions={product?.promotions}
                customizable={customizable}
                product={product}
                dark={darkMode}
                className="tw-flex tw-flex-wrap tw-px-6 tw-justify-center"
                isNewTemplate={isNewTemplate}
              />
            )}
          </div>
          <div
            className={classNames(
              'tw-z-10 tw-w-full tw-flex tw-flex-col tw-gap-10',
              PDPV2Light && 'tw-bg-warm-100',
            )}
          >
            <div className="tw-flex tw-flex-col tw-h-full tw-w-full tw-z-0 tw-gap-y-6 tw-items-center tw-justify-center">
              <div
                className={classNames(
                  'tw-relative tw-w-full tw-z-10 tw-flex tw-items-center tw-justify-stretch tw-bg-center tw-bg-contain tw-bg-no-repeat',
                  darkMode
                    ? DARK_BACKGROUND_IMAGE[template as keyof typeof DARK_BACKGROUND_IMAGE]
                    : BACKGROUND_IMAGE[template as keyof typeof BACKGROUND_IMAGE],
                  hasSpinSet && 'md:tw-h-[calc(100vh_-_76px)]',
                )}
                data-testID="image-wrapper"
              >
                {hasSpinSet ? (
                  <SpinSetWrapper
                    product={product}
                    productList={productList}
                    display={display}
                    encodedSpinSetUrl={encodedSpinSetUrl}
                    images={product.transparentImages!}
                    dark={darkMode}
                  />
                ) : (
                  <PDPHeroCarousel
                    images={[
                      ...(product.images ?? []),
                      ...(product.transparentImages ?? []),
                      ...(lifestyleImages ?? []),
                    ]}
                    openGalleryDialog={openGalleryDialog}
                    dark={darkMode}
                  />
                )}
              </div>
              {product.shortDescription && (
                <div
                  className={classNames(
                    'tw-text-left md:tw-hidden tw-px-6 tw-text-inherit',
                    'md:tw-bg-transparent',
                  )}
                >
                  <Markdown bodySize="large" className={classNames(darkMode && 'tw-text-white')}>
                    {product.shortDescription}
                  </Markdown>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={classNames(
            'lg:tw-pr-16 md:tw-pl-[3vw] md:tw-min-h-full',
            darkMode && 'tw-bg-neutral-800',
            PDPV2Light && 'tw-bg-warm-200',
            !darkMode && !PDPV2Light && 'tw-bg-warm-100',
          )}
        >
          <div className="tw-px-6 md:tw-px-0 md:tw-mr-4 md:tw-pb-15 md:tw-sticky tw-top-0 tw-self-start tw-py-6 md:tw-py-20">
            <DynamicComponent
              _componentTypes={{
                [PDPTemplate.TierOne]: TierOneProductSelection,
                [PDPTemplate.TierTwo]: TierTwoProductSelection,
                [PDPTemplate.TierThree]: TierThreeProductSelection,
              }}
              _field="template"
              _defaultComponent={ProductSelection}
              dark={darkMode}
              product={product}
              template={template}
              essentials={essentials}
              prop65={prop65}
              promoMarkdown={promoMarkdown}
              alternateColors={alternateColorsV2}
              ownerManual={ownerManual}
              alternateSize={sizes?.[0]}
              customizable={customizable}
              compatibleWith={compatibleWith}
            />
          </div>
        </div>
        <GalleryDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          images={dialogImages}
          useImagePadding={useImagePadding}
        />
      </div>
    </div>
  );
};

export default ProductPage;
